/**
 * 19-column CSV export builder (SCRUM-210), ported from Ryan's ccExportCSV.
 *
 * The `portfolio` column uses the customer's real portfolioId natural key
 * (SCRUM-204) rather than the internal UUID. Messaging columns are broadcast
 * (the same campaign name / email / talk track on every row).
 */
import type { Customer } from '@/features/intelligence-dashboard/customer.schema';
import type { Product } from '@/features/product-scan/product-scan.schema';

import { customerHolderAge } from '../customer-derived';
import { customerEligibility } from '../customer-eligibility';

/** The 19 export columns, in exact order. */
export const CSV_COLUMNS: readonly string[] = [
  'portfolio',
  'customer_name',
  'account_type',
  'holder_age',
  'tenure_months',
  'balance',
  'branch',
  // SCRUM-956: region rides alongside branch so a region-targeted list exports
  // the dimension it was built on.
  'region',
  'products_held',
  'last_campaign',
  'last_campaign_days_ago',
  'eligibility_met',
  'eligibility_total',
  'campaign_name',
  'product_name',
  'product_sku',
  'product_category',
  'email_subject',
  'email_body',
  'talk_track',
];

/** Quotes a CSV value when it contains a comma, quote, or newline (RFC 4180). */
export function csvEscape(value: string | number | null | undefined): string {
  if (value == null) return '';
  const s = String(value);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export interface CsvCampaign {
  campaignName: string;
  product: Product | null;
  emailSubject: string;
  emailBody: string;
  talkTrack: string;
}

/** Builds the full CSV text (header + one row per target customer). */
export function buildCsv(targets: Customer[], campaign: CsvCampaign): string {
  const { product } = campaign;
  const rows: string[] = [CSV_COLUMNS.map(csvEscape).join(',')];

  for (const c of targets) {
    const elig = product ? customerEligibility(c, product) : { met: 0, total: 0 };
    const row: (string | number | null)[] = [
      c.portfolioId,
      c.name,
      c.accountType,
      customerHolderAge(c) ?? '',
      c.ageMo,
      c.balance,
      c.branch || '',
      c.region || '',
      c.holdings.join('|'),
      c.lastCampaign ? c.lastCampaign.name : '',
      c.lastCampaign ? c.lastCampaign.daysAgo : '',
      elig.met,
      elig.total,
      campaign.campaignName,
      product ? product.name : '',
      product ? product.sku : '',
      product ? product.category : '',
      campaign.emailSubject,
      campaign.emailBody,
      campaign.talkTrack,
    ];
    rows.push(row.map(csvEscape).join(','));
  }

  return rows.join('\n');
}

/** Builds the download filename: "{slug}-{YYYY-MM-DD}.csv". */
export function csvFilename(campaignName: string, now: Date = new Date()): string {
  const stamp = `${String(now.getFullYear())}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
  const slug =
    campaignName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'campaign';
  return `${slug}-${stamp}.csv`;
}

/** Triggers a browser download of the CSV text. */
export function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1000);
}
